// MapCache service using IndexedDB (via idb library)

import { openDB, DBSchema, IDBPDatabase } from 'idb';

interface MapCache {
  data: any[];
  timestamp: number;
}

interface MapCacheDB extends DBSchema {
  mapCache: {
    key: string;
    value: MapCache;
  };
}

const DB_NAME = 'vn-pixel-map-cache-db';
const DB_VERSION = 1;
const STORE_NAME = 'mapCache';
const CACHE_KEY = 'vn-pixel-map-cache';
const OLD_CACHE_KEY = 'vn-pixel-map-cache'; // LocalStorage key for migration

class MapCacheService {
  private db: IDBPDatabase<MapCacheDB> | null = null;

  /**
   * Initialize IndexedDB database
   */
  async init(): Promise<void> {
    if (this.db) {
      return; // Already initialized
    }

    try {
      this.db = await openDB<MapCacheDB>(DB_NAME, DB_VERSION, {
        upgrade(db) {
          // Create object store if it doesn't exist
          if (!db.objectStoreNames.contains(STORE_NAME)) {
            db.createObjectStore(STORE_NAME);
          }
        },
      });
      console.log('[MAP-CACHE] IndexedDB initialized');

      // Migrate data from LocalStorage on first init
      await this.migrateFromLocalStorage();
    } catch (error) {
      console.error('[MAP-CACHE] Error initializing IndexedDB:', error);
      throw error;
    }
  }

  /**
   * Migrate data from LocalStorage to IndexedDB
   */
  private async migrateFromLocalStorage(): Promise<void> {
    try {
      // Check if data exists in LocalStorage
      const cached = localStorage.getItem(OLD_CACHE_KEY);
      if (!cached) {
        return; // No data to migrate
      }

      // Check if data already exists in IndexedDB
      const existing = await this.getMapCache();
      if (existing) {
        console.log('[MAP-CACHE] Data already exists in IndexedDB, skipping migration');
        // Remove from LocalStorage after successful migration check
        localStorage.removeItem(OLD_CACHE_KEY);
        return;
      }

      // Parse and migrate data
      const cache: MapCache = JSON.parse(cached);
      await this.saveMapCache(cache.data, cache.timestamp);
      console.log('[MAP-CACHE] Migrated data from LocalStorage to IndexedDB');

      // Remove from LocalStorage after successful migration
      localStorage.removeItem(OLD_CACHE_KEY);
    } catch (error) {
      console.error('[MAP-CACHE] Error migrating from LocalStorage:', error);
      // Don't throw - migration failure shouldn't break the app
    }
  }

  /**
   * Get cached map data from IndexedDB
   */
  async getMapCache(): Promise<MapCache | null> {
    try {
      if (!this.db) {
        await this.init();
      }

      if (!this.db) {
        return null;
      }

      const cache = await this.db.get(STORE_NAME, CACHE_KEY);
      return cache || null;
    } catch (error) {
      console.error('[MAP-CACHE] Error reading cache:', error);
      return null;
    }
  }

  /**
   * Save map data to IndexedDB
   */
  async saveMapCache(data: any[], timestamp: number): Promise<void> {
    try {
      if (!this.db) {
        await this.init();
      }

      if (!this.db) {
        throw new Error('Database not initialized');
      }

      const cache: MapCache = { data, timestamp };
      await this.db.put(STORE_NAME, cache, CACHE_KEY);
      console.log('[MAP-CACHE] Saved cache with timestamp:', timestamp);
    } catch (error) {
      console.error('[MAP-CACHE] Error saving cache:', error);
      throw error;
    }
  }

  /**
   * Clear cached map data
   */
  async clearCache(): Promise<void> {
    try {
      if (!this.db) {
        await this.init();
      }

      if (!this.db) {
        return;
      }

      await this.db.delete(STORE_NAME, CACHE_KEY);
      console.log('[MAP-CACHE] Cache cleared');
    } catch (error) {
      console.error('[MAP-CACHE] Error clearing cache:', error);
    }
  }
}

export const mapCacheService = new MapCacheService();
export default mapCacheService;

